import type { StateCreator } from 'zustand';
import {
  fetchModels,
  keepModelPreference,
  setModelPreference,
  toChatModelInfo,
  type ChatModelInfo,
  type ModelDiscoveryResponse,
  type ModelPreference,
  type RecommendedModel,
} from '@/lib/api/providers.client';
import { useBudgetStore } from '@/lib/stores/budget.store';
import {
  announceModelFallback,
  discoverySelection,
  fallbackModel,
  storedCatalog,
} from '@/lib/stores/chat-store/chat-model';
import type { ChatState, GetChatState, SetChatState } from '@/lib/stores/chat.store';

type ModelTier = ModelPreference['tiers'][0]['tier'];

/** The model catalog and the model selection of the chat store (KAN-1266). */
interface ModelsData {
  selectedModel: string;
  // Timestamp of the last manual model pick (dropdown) since the last new
  // chat; 0 when the selection came from the default. A pick holds for the
  // chat it was made in only: a new chat starts on ``defaultChatModel``.
  modelSelectedAt: number;
  availableModels: ChatModelInfo[];
  recommendedModels: RecommendedModel[];
  // What new chats start with: the user's own default model if they keep
  // one, otherwise the workspace Standard model (resolved by the API).
  defaultChatModel: string;
  // Personal Premium/Standard models and pending "recommendation changed" notices.
  modelPreference: ModelPreference | null;
  isLoadingModels: boolean;
  hasLoadedModels: boolean;
  modelsPromise: Promise<void> | null;
}

export interface ModelsSlice extends ModelsData {
  setSelectedModel: (model: string) => void;
  /**
   * Resolve the model for a newly opened /chat/new: the default (personal or
   * workspace), or where the user cannot send with it the next model of the
   * fallback order (``fallbackModel``); a pick in an earlier chat does not
   * carry over. Clears the manual-selection timestamp. Returns true if a
   * model was applied.
   */
  applyModelForNewChat: () => boolean;
  loadModels: (force?: boolean) => Promise<void>;
  /** Store the user's own model for a chat tier or image slot; `null` follows the workspace again. */
  setPersonalModel: (tier: ModelTier, id: string | null) => Promise<void>;
  /** Keep the personal model for a tier although the workspace's changed. */
  keepPersonalModel: (tier: ModelTier) => Promise<void>;
}

/**
 * The slice's state before any catalog has loaded, and again after a reset:
 * the new workspace's default then comes in with the next ``loadModels()``.
 * Fresh objects on every call, like the literals they replace.
 */
export const initialModelsState = (): ModelsData => ({
  selectedModel: '',
  modelSelectedAt: 0,
  availableModels: [],
  recommendedModels: [],
  defaultChatModel: '',
  modelPreference: null,
  isLoadingModels: false,
  hasLoadedModels: false,
  modelsPromise: null,
});

// Whether the chat with ``chatId`` is the page on screen.
const isChatOnScreen = (chatId: string): boolean =>
  typeof window !== 'undefined' && window.location.pathname.includes(chatId);

// Take a discovery response into the store and move the selection along
// with it where nothing the user did speaks against that (``discoverySelection``).
const applyDiscovery = (res: ModelDiscoveryResponse, set: SetChatState, get: GetChatState) => {
  set({
    availableModels: res.chat_models.map(toChatModelInfo),
    recommendedModels: res.recommended_models || [],
    defaultChatModel: res.default_chat_model || '',
    modelPreference: res.model_preference ?? null,
  });
  const change = discoverySelection(get(), storedCatalog(get()), {
    now: Date.now(),
    lowBudget: useBudgetStore.getState().lowBudgetMode,
    isOnScreen: isChatOnScreen,
  });
  if (change.selectedModel !== undefined) set({ selectedModel: change.selectedModel });
  if (change.announce)
    void announceModelFallback(change.announce.chatId, change.announce.modelName);
};

const setSelectedModel = (set: SetChatState, get: GetChatState, model: string) => {
  set({ selectedModel: model, modelSelectedAt: Date.now() });
  const currentChat = get().currentChat;
  if (currentChat) {
    set({
      currentChat: {
        ...currentChat,
        model,
      },
    });
  }
};

const applyModelForNewChat = (set: SetChatState, get: GetChatState): boolean => {
  const model = fallbackModel(storedCatalog(get()), useBudgetStore.getState().lowBudgetMode);
  if (!model) return false;
  set({ selectedModel: model, modelSelectedAt: 0 });
  return true;
};

const loadModels = async (set: SetChatState, get: GetChatState, force = false) => {
  // `force` bypasses the once-per-session guard so callers (e.g. the model
  // selector dropdown) can pull a fresh catalog on demand — picks up new
  // models, id swaps, and price updates without a full page reload. The
  // modelsPromise guard below still coalesces concurrent calls into one
  // in-flight fetch, so a forced refetch can't stack on a pending load.
  if (!force && get().hasLoadedModels) return;
  const existing = get().modelsPromise;
  if (existing) return existing;

  const promise = (async () => {
    set({ isLoadingModels: true });
    try {
      const res = await fetchModels();
      applyDiscovery(res, set, get);
      set({ isLoadingModels: false, hasLoadedModels: true });
    } catch {
      set({ isLoadingModels: false });
    } finally {
      set({ modelsPromise: null });
    }
  })();

  set({ modelsPromise: promise });
  return promise;
};

const setPersonalModel = async (
  set: SetChatState,
  get: GetChatState,
  tier: ModelTier,
  modelId: string | null
) => {
  applyDiscovery(await setModelPreference(tier, modelId), set, get);
};

const keepPersonalModel = async (set: SetChatState, get: GetChatState, tier: ModelTier) => {
  applyDiscovery(await keepModelPreference(tier), set, get);
};

export const createModelsSlice: StateCreator<ChatState, [], [], ModelsSlice> = (set, get) => ({
  ...initialModelsState(),
  setSelectedModel: (model) => setSelectedModel(set, get, model),
  applyModelForNewChat: () => applyModelForNewChat(set, get),
  loadModels: (force) => loadModels(set, get, force),
  setPersonalModel: (tier, modelId) => setPersonalModel(set, get, tier, modelId),
  keepPersonalModel: (tier) => keepPersonalModel(set, get, tier),
});
